import { Box, Stack } from '@mui/material';
import { type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { getColor } from '../../helpers/color';
import { achievementCreatePath, categoryCreatePath, postponeEditPath, questCreatePath } from '../../navigation/paths';
import { VoiceAction } from '../../types/voice';
import { ActionCardDeleteButton } from './ActionCardDeleteButton';
import { ActionCardIcon } from './ActionCardIcon';
import { ActionCardView } from './ActionCardView';

const ACTION_ICONS: Record<string, string> = {
  CREATE_QUEST: 'add_circle',
  CREATE_CATEGORY: 'category',
  POSTPONE_QUEST: 'schedule',
};

const ACTION_COLORS: Record<string, { bg: string; icon: string }> = {
  CREATE_QUEST: {
    icon: getColor('LIGHTGREEN_600'),
    bg: `color-mix(in srgb, ${getColor('LIGHTGREEN_600')} 18%, transparent)`,
  },
  CREATE_CATEGORY: {
    icon: getColor('PURPLE_500'),
    bg: `color-mix(in srgb, ${getColor('PURPLE_500')} 18%, transparent)`,
  },
  POSTPONE_QUEST: {
    icon: getColor('BLUE_500'),
    bg: `color-mix(in srgb, ${getColor('BLUE_500')} 18%, transparent)`,
  },
};

interface ActionCardProps {
  action: VoiceAction;
  dependentActions?: VoiceAction[];
  extraRemoveCount?: number;
  onRemove: (id: string) => void;
  animDelay?: number;
}

export const ActionCard: FC<ActionCardProps> = ({
  action,
  dependentActions,
  extraRemoveCount = 0,
  onRemove,
  animDelay = 0,
}) => {
  const navigate = useNavigate();
  const icon = ACTION_ICONS[action.type] ?? 'bolt';
  const colors = ACTION_COLORS[action.type] ?? { bg: 'rgba(255,255,255,0.1)', icon: 'var(--tg-theme-button-color)' };
  const handleEditClick = () => {
    if (action.type === 'POSTPONE_QUEST') {
      navigate(`${postponeEditPath}?voiceActionId=${action.id}`);
    } else if (action.type === 'CREATE_CATEGORY') {
      navigate(`${categoryCreatePath}?voiceActionId=${action.id}`);
    } else if (action.type === 'CREATE_ACHIEVEMENT') {
      navigate(`${achievementCreatePath.replace('/:categoryId?', '')}?voiceActionId=${action.id}`);
    } else {
      navigate(`${questCreatePath.replace('/:categoryId?', '')}?voiceActionId=${action.id}`);
    }
  };

  return (
    <Box
      sx={{
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        overflow: 'hidden',
        animation: `voiceFadeInUp 0.28s ease ${animDelay}ms both`,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.25} sx={{ p: 1.5 }}>
        <Box
          component="button"
          onClick={handleEditClick}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <ActionCardIcon action={action} actionIcon={icon} colors={colors} />
          <ActionCardView action={action} dependentActions={dependentActions} />
        </Box>
        <ActionCardDeleteButton
          onRemove={() => onRemove(action.id)}
          extraRemoveCount={extraRemoveCount}
        />
      </Stack>
    </Box>
  );
};
